import React, { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import pic1 from './images/pic1.png';
import pic2 from './images/pic2.png';
import pic3 from './images/pic3.png';
import pic4 from './images/pic4.png';
import pic5 from './images/pic5.png';

// Chapters shown in the contents pane. Slugs MUST stay in sync with
// the id="…" attributes on the corresponding <h2> headings below —
// the anchors break silently if the two drift apart. Heading text
// here is independent and can be reworded without touching ids.
const CHAPTERS = [
  { id: 'creating-a-model', label: 'Creating a Model' },
  { id: 'executing-a-model', label: 'Executing a Model' },
  { id: 'adding-ticker-to-the-package', label: 'Adding ticker to the package' },
];

export function Help() {
  const navigate = useNavigate();

  // Clicking a TOC entry must (a) keep us on /help — a plain <a href="#x">
  // would cause the browser to do a full reload to "/#x", dropping the
  // current route — and (b) still scroll to the heading. We navigate via
  // react-router with a hash-only target so the /help route is preserved
  // and then imperatively scroll, which also makes re-clicking the same
  // chapter still jump (React Router 6 no-ops duplicate {hash} links).
  const handleChapterClick = useCallback(
    (event, id) => {
      // Allow modifier-clicks (Ctrl/Cmd/middle) to open in a new tab etc.
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) {
        return;
      }
      event.preventDefault();

      // Update the URL hash without re-mounting the component so the
      // browser back/forward buttons keep working.
      navigate(`#${id}`);

      // Smooth-scroll to the heading. The page header is fixed-padding,
      // so we offset by a small margin to avoid the heading hiding under
      // a sticky navbar.
      const target = document.getElementById(id);
      if (target) {
        const headerOffset = 72;
        const elementPosition = target.getBoundingClientRect().top;
        const offsetPosition = elementPosition + window.pageYOffset - headerOffset;
        window.scrollTo({ top: offsetPosition, behavior: 'smooth' });
      }
    },
    [navigate]
  );

  return (
    <div className="container-fluid py-4" style={{ maxWidth: '1100px' }}>
      <h1 className="mb-4">Help</h1>

      <div className="row">
        {/* Contents pane. `sticky-top` keeps it pinned on scroll so the
            user can jump between chapters from anywhere on the page.
            On small viewports it stacks above the content. */}
        <nav
          className="col-md-3 mb-4"
          aria-label="Help contents"
        >
          <div
            className="bg-light border rounded p-3 sticky-top"
            style={{ top: '1rem' }}
          >
            <h2 className="h6 text-uppercase text-muted mb-3">
              Contents
            </h2>
            <ul className="list-unstyled mb-0">
              {CHAPTERS.map((c) => (
                <li key={c.id} className="mb-2">
                  <a
                    href={`#${c.id}`}
                    onClick={(e) => handleChapterClick(e, c.id)}
                    className="text-decoration-none"
                  >
                    {c.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        </nav>

        {/* Main article column — the chapter body itself. */}
        <div className="col-md-9" style={{ maxWidth: '860px' }}>

        <h2 id="creating-a-model" className="mt-4 mb-3">Creating a Model</h2>
        <p>
          To create a model: open the Admin dashboard and click <em>Create New Model</em> on the Models page.
        </p>
        <ul>
          <li>
            Open the Admin dashboard.<br/>
            <img src={pic1} alt="Admin dashboard" className="img-fluid border rounded mb-3" style={{ width: '25%' }} />
          </li>
          <li>
            Open Models page.<br/>
            <img src={pic2} alt="Models page" className="img-fluid border rounded mb-3" style={{ width: '25%' }} />
          </li>
          <li>
            Click <em>Create New Model</em> button on the Models page.<br/>
            <img src={pic3} alt="Create New Model button" className="img-fluid border rounded mb-3" style={{ width: '80%' }} />
          </li>
        </ul>
        <p>The following fields are available:</p>

        <table className="table table-bordered table-sm">
          <thead className="thead-light">
            <tr>
              <th style={{ width: '220px' }}>Field</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td><strong>Name</strong></td>
              <td>
                A human-readable label for the model. Optional, but recommended so you can
                identify the model quickly in lists and execution history.
              </td>
            </tr>
            <tr>
              <td><strong>Description</strong></td>
              <td>
                Free-text notes about the model's purpose, data sources, or any other context
                you want to preserve. Optional.
              </td>
            </tr>
            <tr>
              <td><strong>Enable daily automatic execution</strong></td>
              <td>
                When checked, the scheduler will run this model once per trading day
                (end-of-day batch) without any manual intervention. The model must have a
                valid <em>Active Signal Tag</em> configured for this to work.
              </td>
            </tr>
            <tr>
              <td><strong>Realtime Candles Automatic Execution Interval (minutes)</strong></td>
              <td>
                If set to a positive integer, the model will also be executed repeatedly
                during trading hours at the specified interval (e.g. every 5 minutes), using
                freshly collected real-time candles each time. Leave empty to disable
                intra-day automatic execution. This option works alongside — not instead of —
                the daily automatic execution checkbox.
              </td>
            </tr>
            <tr>
              <td><strong>Public</strong></td>
              <td>
                Makes the model visible to all users on the platform. When unchecked, only
                the owner and users listed in <em>Visible to Users</em> can see or execute it.
              </td>
            </tr>
            <tr>
              <td><strong>Visible to Users</strong></td>
              <td>
                A multi-select list of individual users who should have access to this model
                even though it is not public. Hold <kbd>Ctrl</kbd> / <kbd>Cmd</kbd> to select
                multiple entries. Leave empty for no extra sharing.
              </td>
            </tr>
            <tr>
              <td><strong>Signal Tags</strong></td>
              <td>
                Read-only. Tags are extracted automatically from the uploaded <code>.sd2</code> file
                and represent the named output scenarios defined inside the algorithm. You
                cannot edit this list directly; upload a new file to refresh it.
              </td>
            </tr>
            <tr>
              <td><strong>Active Signal Tag</strong></td>
              <td>
                The tag whose output is treated as the model's primary result. When the model
                is executed, all tags are run, but the <em>Active Signal Tag</em> result is
                highlighted and used for subscription notifications. You can type a custom
                value or pick from the list of tags read from the file.
              </td>
            </tr>
            <tr>
              <td><strong>File (.sd2)</strong></td>
              <td>
                The algorithm file in SD2 format. Required when creating a new model. When
                editing, leave the field empty to keep the existing file, or upload a new one
                to replace it. The file is stored in the database and transferred to the
                remote Windows execution host at run time.
              </td>
            </tr>
          </tbody>
        </table>

        <h2 id="executing-a-model" className="mt-5 mb-3">Executing a Model</h2>
        <p>
          To execute a model, open <em>Admin Dashboard</em>, navigate to <em>Models</em> page, click <em>Model Execution</em> tab, and click the <em>Execute</em> button<br/>
          <img src={pic4} alt="Execute model" className="img-fluid border rounded mb-3" style={{ width: '80%' }} />
        </p>

        <h4 className="mt-4">Execution Options</h4>

        <h5 className="mt-3">Original CTRAC Package</h5>
        <p>
          When this checkbox is checked, the execution skips package generation entirely and
          instead uses a pre-built, static CTRAC archive that was shipped together with the
          application (<em>OriginalCtracPackageService</em>). This is the reference dataset the
          algorithm was originally developed and tested against. Use it when you need to reproduce
          a historical run with exactly the same data — for example, to validate a newly uploaded
          model against a known baseline — or when no up-to-date candle data is available in the
          database. Because the package is fixed, neither real-time candles nor today's historical
          data are incorporated.
        </p>

        <h5 className="mt-3">Realtime Candles</h5>
        <p>
          When this checkbox is checked (and <em>Original CTRAC Package</em> is <strong>not</strong>
          {' '}checked), the platform first pulls the latest intra-day candles from live market-data
          sources for every ticker referenced by the model. Those candles — marked{' '}
          <code>Realtime = true</code> in the database — are then included in the freshly generated
          CTRAC package. This produces the most current possible input data and is the recommended
          option during trading hours. When unchecked, only the end-of-day historical candles
          already stored in the database are used, which is the default for overnight batch runs.
        </p>

        <p>
          Click <em>Signal Files</em> tab to see the resulting signal files from all past executions of this model.<br/>
          <img src={pic5} alt="Signal files" className="img-fluid border rounded mb-3" style={{ width: '80%' }} />
        </p>


        <h3 className="mt-5 mb-3">How it works</h3>
        <p>
          Executing a model sends the algorithm to a remote Windows host, runs the
          {' '}<strong>wwms.exe</strong> trading engine against a CTRAC data package, and stores the
          resulting signal file back in the database. The following steps happen under the hood:
        </p>

        <ol>
          <li className="mb-2">
            <strong>Model is fetched</strong> — the platform retrieves the model record and its
            attached <code>.sd2</code> file from the database.
          </li>
          <li className="mb-2">
            <strong>Real-time candles are collected</strong> (optional, see checkbox below) —
            if real-time candle collection is requested, the platform resolves every ticker that
            the model's SD2 file references and fetches the latest intra-day OHLCV bars from the
            configured market-data sources before building the data package.
          </li>
          <li className="mb-2">
            <strong>CTRAC data package is prepared</strong> — a ZIP archive in the proprietary
            CTRAC format is assembled from database candles. If the package already exists on the
            remote machine and is current for the day, this step is skipped to save time.
          </li>
          <li className="mb-2">
            <strong>wwms.exe binaries are synchronised</strong> — the platform compares a local
            MD5 checksum against the checksum cached on the remote Windows host. If they differ,
            the binaries are re-uploaded via SFTP and any running <em>wwms.exe</em> process is
            terminated first to avoid locked-file errors.
          </li>
          <li className="mb-2">
            <strong>Model file is transferred</strong> — the <code>.sd2</code> file is uploaded
            to the remote working directory.
          </li>
          <li className="mb-2">
            <strong>wwms.exe runs once per Signal Tag</strong> — for each tag defined in the
            model, <em>wwms.exe</em> is invoked with the tag name, model path, and output path as
            arguments. The resulting <code>.sig</code> signal file is downloaded via SFTP and
            saved to the database.
          </li>
        </ol>


        <h2 id="adding-ticker-to-the-package" className="mt-5 mb-3">Adding ticker to the package</h2>
        <p>
          Every data package (CTRAC, CSV, SFX exe, …) is built from the rows listed on the
          {' '}<strong>Admin → Package Tickers</strong> page. Each row binds a single underlying
          ticker to a package-visible name, code, sub-folder and interval strategy, so the
          package contents are fully admin-driven. Open the page from{' '}
          <em>Admin Dashboard → Package Tickers</em> and use the <em>+ Add Package Ticker</em>
          {' '}button to create a new row.
        </p>
        <p>
          The Add / Edit modal exposes the following fields:
        </p>
        <table className="table table-bordered table-sm">
          <thead className="thead-light">
            <tr>
              <th style={{ width: '220px' }}>Field</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td><strong>Ticker</strong></td>
              <td>
                The underlying ticker this row will pull candles from. Type at least one
                character to search; pick one from the dropdown. The pick auto-fills the
                Name / Code fields, which you can still override. Ticker link cannot be
                changed after creation — delete the row and re-create to re-link.
              </td>
            </tr>
            <tr>
              <td><strong>Name</strong></td>
              <td>
                Display name written into the generated package. Defaults to the underlying
                ticker's name when you pick it, but can be overridden.
              </td>
            </tr>
            <tr>
              <td><strong>Code</strong></td>
              <td>
                Display code (symbol) written into the generated package. Defaults to the
                underlying ticker's code when you pick it, but can be overridden.
              </td>
            </tr>
            <tr>
              <td><strong>Subfolder</strong></td>
              <td>
                Sub-folder inside the generated package where this ticker is placed (e.g.
                {' '}<code>DATA</code>, <code>MD</code>, <code>BOND</code>). Different sub-folders
                let you partition the same ticker into multiple logical sections of the
                package.
              </td>
            </tr>
            <tr>
              <td><strong>Interval</strong></td>
              <td>
                Which candle series feeds the package for this row. <em>Daily</em> uses Day
                candles as-is. <em>Weekly</em> uses pre-aggregated Week candles. <em>Weekly
                (combined from daily candles)</em> derives weekly bars from the Day series
                at build time, so you only need Day candles in the database.
              </td>
            </tr>
            <tr>
              <td><strong>Start date</strong></td>
              <td>
                Optional per-ticker floor for package generation. When set, the package
                builder reads candle rows starting from the <em>later</em> of this date and
                the global <em>TimeFrom</em> — so the field can only narrow the package window,
                never widen it. Leave empty to inherit the global TimeFrom.
              </td>
            </tr>
            <tr>
              <td><strong>Exclude columns</strong></td>
              <td>
                Tick the package-output columns that should be dropped for this ticker
                (OPEN, HIGH, LOW, CLOSE, VOL, OI). <code>DATE</code> is always included.
                Useful when a downstream consumer only needs a subset of fields. The
                package builder silently ignores unknown values so stale entries cannot
                break the build.
              </td>
            </tr>
            <tr>
              <td><strong>Volume divisor</strong></td>
              <td>
                Optional pre-scale applied to the raw candle <code>Volume</code> before
                it is written into the generated package. Pick a positive integer (10,
                100, 1000, 10000, …) and the package builder divides the raw volume by
                that number and rounds to the nearest integer before serialising. Use
                this when the downstream consumer only needs thousands / tens-of-thousands
                of shares and the raw value would overflow the package format's cell
                width. Choose <em>As is — do not divide</em> (the default) to write the
                raw volume unchanged.
              </td>
            </tr>
            <tr>
              <td><strong>Column names mapping</strong></td>
              <td>
                Optional per-row rename map for the canonical package-output columns.
                Keys are the canonical CTRAC / CSV column names (<code>OPEN</code>,
                {' '}<code>HIGH</code>, <code>LOW</code>, <code>CLOSE</code>,{' '}
                <code>VOL</code>, <code>OI</code>); values are the alias names that
                should appear in the generated package header (CTRAC DOP file / CSV
                first row). Multiple keys can target the same alias so a downstream
                consumer can collapse several inputs into a single column (e.g. mapping
                {' '}<code>HIGH</code>, <code>LOW</code> and <code>CLOSE</code> all to{' '}
                <code>C</code>). <code>DATE</code> is always written as <code>DATE</code>{' '}
                and cannot be remapped. Columns listed in <em>Exclude columns</em> are
                removed from the package output before the map is consulted, so a mapping
                for an excluded column has no effect on the output. Unknown canonical
                keys are silently ignored, and map entries whose alias matches the
                canonical key (case-insensitive) are dropped so the field only carries
                real rewrites. Leave empty to use the canonical names — the pre-existing
                behaviour.
              </td>
            </tr>
          </tbody>
        </table>
        <p className="text-muted small">
          <strong>How the three column-shaping fields interact.</strong> The package
          builder applies them in this fixed order: first, any column ticked in
          {' '}<em>Exclude columns</em> is dropped from the output. Then, for each
          remaining candle, the <em>Volume divisor</em> (if any) is applied to the raw{' '}
          <code>Volume</code> value and rounded to the nearest integer. Finally, the
          {' '}<em>Column names mapping</em> is consulted to rename the surviving
          columns in the emitted header — so a mapping for an excluded column is
          never seen, and a mapping that would only re-spell a column as itself is
          treated as a no-op.
        </p>

        <h4 className="mt-4">Automatic enabling of candle collection</h4>
        <p>
          The <em>Collect Candles</em> flag on the underlying ticker controls whether the
          daily / weekly / monthly candle-collection scheduler picks that ticker up at all.
          When you create a new package ticker row whose underlying ticker has{' '}
          <code>CollectCandles = false</code>, the platform does the following automatically
          so the new row will actually have data the next time a package is generated:
        </p>
        <ol>
          <li className="mb-2">
            <strong>Flips <code>CollectCandles</code> from <code>false</code> to{' '}
            <code>true</code></strong> on the underlying ticker, and saves it. This lets the
            regular candle-collection scheduler keep picking the symbol up going forward.
          </li>
          <li className="mb-2">
            <strong>Finds the latest existing daily candle</strong> for that ticker in the
            database (or reports that none exist).
          </li>
          <li className="mb-2">
            <strong>Enqueues a backfill candle-collection job</strong> on the
            {' '}<em>Jobs</em> RabbitMQ queue, scoped to this single ticker:
            <ul className="mt-2">
              <li>
                If at least one candle exists, the backfill starts from the day{' '}
                <em>after</em> the latest existing candle — so we don't re-fetch data we
                already have.
              </li>
              <li>
                If no candles exist at all, the backfill starts from{' '}
                <code>1900-01-01</code> and the upstream data source decides how far back it
                can actually return.
              </li>
              <li>
                The end date is "today" (UTC) and the interval is daily.
              </li>
            </ul>
          </li>
        </ol>

        <p>
          On the UI, the green success alert after a successful create reads, for example:
        </p>
        <pre className="bg-light p-2 border rounded small mb-3"><code>{`Package ticker #42 (AAPL) created. CollectCandles was disabled on the underlying
ticker and has been enabled; a candle backfill job was enqueued starting from
2024-08-01.`}</code></pre>
        <p>
          The exact <em>starting from</em> date is either the day after the latest existing
          candle or <code>1900-01-01</code> if no candles exist yet. You can monitor the
          backfill progress from <em>Admin Dashboard → Scheduler Logs</em> (filter by tag
          {' '}<code>candle-collector</code>); once it finishes, the next package generation
          will include this ticker with a complete daily series.
        </p>

          </div>{/* /col-md-9 (article) */}
        </div>{/* /row */}
      </div>
  );
}

